import {
  generateGuestLocationEndPoint,
  messages,
  TwilioVerificationChannelEnum,
  User,
  useToggle,
  useTwilioSendCodeMutation,
} from '@alluradirect/react';
import * as Sentry from '@sentry/nextjs';
import AlluraTurnstile from 'components/layout/Page/components/AlluraTurnstile';
import { SignupCreateAccountForm } from 'components/views/pages/signup/components/SignupCreateAccountForm';
import { verifyTurnstileToken } from 'components/views/pages/_general/helpers';
import { customQuery } from 'config/reactQuery/reactQuery';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAppUserLoginMutation } from 'hooks/api/user/mutations/auth/useAppUserLogin';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';

const initialValues = {
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  marketing_emails: true,
  is_email_taken: false,
};

export type SignupCreateAccountFormikValues = typeof initialValues;

const validationSchema = yup.object().shape({
  email: yup.string().email().min(3).max(100).required().label('Email'),
  first_name: yup.string().max(100).required().label('First Name'),
  last_name: yup.string().max(100).required().label('Last Name'),
  password: yup
    .string()
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .min(8, 'Must be at least 8 characters long')
    .max(143, 'Maximum 143 characters long')
    .required(messages.requiredValue)
    .label('Password'),
  marketing_emails: yup.boolean().required().label('Promotions'),
  is_email_taken: yup.boolean().isFalse().label('Email Exists'),
});

interface Props {
  initialEmail?: string;
  hasTopAppDivider?: boolean;
  hasFooter?: boolean;
  isEmailFieldDisabled?: boolean;
  navigateOnSuccess: (data: User) => void;
}

export const SignupCreateAccount = ({
  initialEmail,
  hasTopAppDivider = true,
  hasFooter = true,
  isEmailFieldDisabled = false,
  navigateOnSuccess,
}: Props) => {
  const toggleEmailVerification = useToggle();
  const qClient = useQueryClient();

  const sendCode = useTwilioSendCodeMutation();
  const userLoginMutation = useAppUserLoginMutation();

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");

  const onVerificationSuccess = async (
    values: SignupCreateAccountFormikValues,
    helpers: FormikHelpers<SignupCreateAccountFormikValues>,
  ) => {
    // eslint-disable-next-line no-unused-vars
    const { is_email_taken, ...rest } = values;
    // Converted this from async/await to onSuccess.
    // The async/await method is cleaner, but I noticed that it does not handle errors correctly.
    // If you turn off the Django server you can still get through the cascade of requests.
    userLoginMutation.mutate(
      { email: rest.email, password: rest.password },
      {
        onSuccess: async (data: { DATA: { USER: User; }; }) => {
          const guestId = data.DATA.USER.ID;
          const locationData = await customQuery(generateGuestLocationEndPoint(guestId)).catch(e => {
            Sentry.captureException(e);
            helpers.setSubmitting(false);
          });
          if (locationData) {
            qClient.setQueryData(generateGuestLocationEndPoint(guestId), locationData);
          }

          navigateOnSuccess && navigateOnSuccess(data.DATA.USER);
        },
      },
    );
  };

  const onSubmit = async (values: SignupCreateAccountFormikValues, helpers: any) => {
    try {

      if (!captchaToken) {
        setStatus("Please complete the safety check.");
        return;
      }

      setStatus("Sending to server...");

      try {
        // Send the token directly to your external backend server
        const data = await verifyTurnstileToken(captchaToken, 'signup');

        // customQuery handles throwing errors if the network fails.
        // It returns the parsed JSON data directly.
        if (data?.SUCCESS && data?.DATA?.SUCCESS) {
          setStatus("Logged in successfully!");
          await sendCode.mutateAsync({
            channel: TwilioVerificationChannelEnum.email,
            value: values.email,
          });
          toggleEmailVerification.open();

        } else {
          setStatus("Server rejected the request or captcha failed.");
        }
      } catch (error) {
        setStatus("Error connecting to the backend server.");
      }


    } catch (e) {
      Sentry.captureException(e);
      toast.error('Failed to send confirmation code');
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const initValues: SignupCreateAccountFormikValues = { ...initialValues, email: initialEmail ?? initialValues.email };

  return (
    <Formik initialValues={initValues} onSubmit={onSubmit} validationSchema={validationSchema} isInitialValid={false}>
      <>
        <Form>
          <SignupCreateAccountForm
            onVerificationSuccess={onVerificationSuccess}
            toggleEmailVerification={toggleEmailVerification}
            hasTopAppDivider={hasTopAppDivider}
            hasFooter={hasFooter}
            isEmailFieldDisabled={isEmailFieldDisabled}
            isCaptchaValid={!!captchaToken}
          >
            <>
              <AlluraTurnstile onVerify={(token) => setCaptchaToken(token)} siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SIGNUP_KEY ?? ''} />
              {status && <p>{status}</p>}
            </>
          </SignupCreateAccountForm>
        </Form>
      </>
    </Formik>
  );
};
